import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import RecordWizard from '../../../../component/reusable/formRecords/RecordWizards';
import { Field as FormField, Select as FormSelect, SectionHead, SectionDivider, ReadonlyBox, CurrencyInput, isCurrencyLabel, INP, INP_LOCKED } from '../../../../component/reusable/formRecords/formFieldPrimitives';
import { MultiToggle as TogglePills } from '../../../shared/forms/FormControls';
import { FilterBar, SearchRow } from '../../../MarketTrad/shared';
import NlimsDataTable from '../../../shared/NlimsDataTable/NlimsDataTable';
import { useAuth } from '../../../../context/AuthContext';
import { useLocationOptions } from '../../../Admin/shared/useLocationOptions';
import { buildConflictRecordPayload, conflictsApi, conflictCommitteesApi } from '../../../../api/conflictsApi';
import { useCollectionSync } from '../../../../hooks/useCollectionSync';
import { ActionDropdown, anchorRect, RejectNotesModal, DeleteConfirmModal, actionCellHtml, WORKFLOW_RULES } from '../../../shared/RowActionMenu';
import FileUploadField from '../../../shared/FileUploadField';
import { useRegistryLabel } from '../../../Admin/FoundationalTables/FormRegistry/registryFieldOverrides';

const CONFLICT_TYPE_OPTIONS = [
  { value: 'grazing_dispute', label: 'Grazing Dispute' },
  { value: 'farm_destruction', label: 'Farm Destruction' },
  { value: 'water_point_clash', label: 'Water Point Clash' },
  { value: 'route_encroachment', label: 'Route Encroachment' },
  { value: 'resource_access', label: 'Resource Access' },
  { value: 'climate_related', label: 'Climate Related' },
  { value: 'traditional_beliefs', label: 'Traditional Beliefs' },
  { value: 'other', label: 'Other' },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const RESOLUTION_OPTIONS = [
  { value: 'unresolved', label: 'Unresolved' },
  { value: 'under_mediation', label: 'Under Mediation' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'escalated_security', label: 'Escalated Security' },
  { value: 'escalated_court', label: 'Escalated Court' },
];

const FIRST_OCCURRENCE_OPTIONS = [
  { value: 'yes_first', label: 'Yes, first occurrence' },
  { value: 'no_repeat', label: 'No, repeat incident' },
];

// Historic rows may still carry the old boolean/short-string values this
// field used to be saved as before it was corrected to the DCIC-13 enum.
function normalizeFirstOccurrence(value) {
  if (value === true || value === 'yes' || value === 'yes_first') return 'yes_first';
  if (value === false || value === 'no' || value === 'no_repeat') return 'no_repeat';
  return 'yes_first';
}

const PARTIES_PRODUCTION_OPTIONS = [
  { value: 'pastoralist', label: 'Pastoralist' },
  { value: 'agropastoral', label: 'Agropastoral' },
  { value: 'sedentary_farmer', label: 'Sedentary Farmer' },
  { value: 'mixed', label: 'Mixed' },
];

const PARTIES_INVOLVED_TYPE_OPTIONS = [
  { value: 'farmer', label: 'Farmer' },
  { value: 'herder', label: 'Herder' },
  { value: 'community', label: 'Community' },
  { value: 'security_agency', label: 'Security Agency' },
  { value: 'traditional_institution', label: 'Traditional Institution' },
  { value: 'ngo_partner', label: 'NGO Partner' },
  { value: 'other', label: 'Other' },
];

const CAUSE_OF_CONFLICT_OPTIONS = [
  { value: 'crop_damage', label: 'Crop Damage' },
  { value: 'water_access', label: 'Water Access' },
  { value: 'grazing_encroachment', label: 'Grazing Encroachment' },
  { value: 'route_blockage', label: 'Route Blockage' },
  { value: 'seasonal_migration', label: 'Seasonal Migration' },
  { value: 'climate', label: 'Climate' },
  { value: 'land_rights', label: 'Land Rights' },
  { value: 'other', label: 'Other' },
];

const SEX_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'unknown', label: 'Unknown' },
];

const AGE_GROUP_OPTIONS = [
  { value: 'youth_under35', label: 'Youth Under 35' },
  { value: 'adult', label: 'Adult' },
  { value: 'mixed', label: 'Mixed' },
];

const REPORTING_AUTHORITY_OPTIONS = [
  { value: 'lga_desk_officer', label: 'LGA Desk Officer' },
  { value: 'security_agency', label: 'Security Agency' },
  { value: 'traditional_leader', label: 'Traditional Leader' },
  { value: 'agro_ranger', label: 'Agro Ranger' },
  { value: 'ngo_partner', label: 'NGO Partner' },
  { value: 'community_peace_committee', label: 'Community Peace Committee' },
];

const VIEW_STATUS_DOT = {
  draft: 'bg-gray-400',
  submitted: 'bg-blue-500',
  validated: 'bg-amber-500',
  approved: 'bg-green-500',
  rejected: 'bg-red-500',
};

const VIEW_STATUS_PILL = {
  draft: 'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-50 text-blue-700',
  validated: 'bg-amber-50 text-amber-700',
  approved: 'bg-green-50 text-green-700',
  rejected: 'bg-red-50 text-red-600',
};

const BADGE_STYLE = {
  draft: 'background:#f3f4f6;color:#6b7280;',
  submitted: 'background:#dbeafe;color:#1d4ed8;border:1px solid #93c5fd;',
  validated: 'background:#fef3c7;color:#b45309;border:1px solid #fcd34d;',
  approved: 'background:#dcfce7;color:#15803d;border:1px solid #86efac;',
  rejected: 'background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;',
  unresolved: 'background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;',
  in_mediation: 'background:#fef3c7;color:#b45309;border:1px solid #fcd34d;',
  resolved: 'background:#dcfce7;color:#15803d;border:1px solid #86efac;',
  escalated: 'background:#ede9fe;color:#6d28d9;border:1px solid #c4b5fd;',
  low: 'background:#dcfce7;color:#15803d;border:1px solid #86efac;',
  medium: 'background:#fef3c7;color:#b45309;border:1px solid #fcd34d;',
  high: 'background:#fed7aa;color:#c2410c;border:1px solid #fb923c;',
  critical: 'background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;',
};

function humanize(value) {
  return String(value ?? '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function displayList(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map((item) => String(item)).join(', ');
  }
  return value ? String(value) : 'â€”';
}

function formatDate(value) {
  if (!value) return 'â€”';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: '2-digit' });
}

function badgeHtml(data, type) {
  if (type !== 'display') return data ?? '';
  const raw = String(data ?? '').trim().toLowerCase();
  const style = BADGE_STYLE[raw] ?? 'background:#f3f4f6;color:#6b7280;';
  const label = humanize(data) || 'â€”';
  return `<span style="display:inline-flex;padding:2px 10px;border-radius:6px;font-size:11px;font-weight:600;${style}">${label}</span>`;
}

function joinParties(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  return value ? String(value) : '';
}

function splitParties(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

function Field({ label, children, span2 = false, required = false, hint }) {
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <FormField label={label} required={required} hint={hint}>
        {children}
      </FormField>
    </div>
  );
}

function Select({ label, value, onChange, options, span2 = false, required = false, readOnly = false, placeholder = 'Select...', hint, onPropose }) {
  const selectNode = (
    <FormSelect
      value={value ?? ''}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      className={INP}
    />
  );
  return (
    <Field label={label} span2={span2} required={required} hint={hint}>
      {readOnly ? (
        <ReadonlyBox>{value || 'Not set'}</ReadonlyBox>
      ) : onPropose ? (
        <div className="flex gap-2">
          <div className="flex-1">{selectNode}</div>
          <button
            type="button"
            onClick={onPropose}
            className="shrink-0 rounded-lg border border-dashed border-green-300 bg-green-50 px-4 py-3 text-[12px] font-semibold text-green-700 hover:bg-green-100"
          >
            + Propose
          </button>
        </div>
      ) : selectNode}
    </Field>
  );
}
function TextInput({ label, value, onChange, type = 'text', span2 = false, readOnly = false, placeholder = '', min }) {
  if (type === 'number' && isCurrencyLabel(label)) {
    return (
      <Field label={label} span2={span2}>
        <CurrencyInput value={value} onChange={onChange} placeholder={placeholder || '0'} readOnly={readOnly} min={min ?? '0'} />
      </Field>
    );
  }
  return (
    <Field label={label} span2={span2}>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        min={min}
        className={readOnly ? INP_LOCKED : INP}
      />
    </Field>
  );
}

function TextArea({ label, value, onChange, span2 = false, placeholder = '' }) {
  return (
    <Field label={label} span2={span2}>
      <textarea
        rows={span2 ? 3 : 2}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`${INP} resize-none`}
      />
    </Field>
  );
}

function ReviewRow({ label: pageLabel, value }) {
  const label = useRegistryLabel(pageLabel);
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
      <span className="text-xs font-semibold text-gray-500">{label}</span>
      <span className="text-sm text-right text-gray-900">{value || value === 0 ? String(value) : '—'}</span>
    </div>
  );
}


export function ConflictEditorModal({ mode, row, isR05, isFieldUser, onClose, onSaved, modulePanel = null, onEdit, canEdit = true }) {
  const [form, setForm] = useState({
    state: '',
    lga: '',
    wardCommunity: '',
    incidentDate: '',
    conflictType: '',
    severityLevel: 'low',
    resolutionStatus: 'unresolved',
    firstOccurrence: 'yes_first',
    partiesInvolved: [],
    partiesProductionType: [],
    sexOfPrimaryParties: '',
    ageGroupOfParties: '',
    cause: '',
    livestockLost: '',
    humanCasualties: '',
    estimatedEconomicLossNgn: '',
    livelihoodImpact: '',
    evidenceAttachment: '',
    evidenceAttachmentFileName: '',
    assetDamage: '',
    reportingAuthority: '',
    linkedCommitteeId: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const [committeeOptions, setCommitteeOptions] = useState([]);
  const isReviewStep = step === 1;
  const lockedLocation = isR05;
  const simplified = isR05;
  const { user } = useAuth();
  const { stateOptions, lgaOptions, wardOptions, loadingLgas, loadingWards } = useLocationOptions(form.state, form.lga);
  const currentDataStatus = (row?.data_status ?? row?.dataStatus ?? 'draft').toLowerCase();

  useEffect(() => {
    conflictCommitteesApi.listAll({ per_page: 200 })
      .then(({ items }) => {
        setCommitteeOptions(
          (items ?? [])
            .filter((c) => c.uuid)
            .map((c) => ({
              value: c.uuid,
              label: [c.committeeName || 'Committee', c.state && `— ${c.state}`].filter(Boolean).join(' '),
            }))
        );
      })
      .catch(() => setCommitteeOptions([]));
  }, []);

  const canGoNext =
    Boolean(form.conflictType) &&
    Boolean(form.state) &&
    Boolean(form.lga) &&
    Boolean(form.wardCommunity.trim()) &&
    Boolean(form.incidentDate) &&
    form.partiesInvolved.length > 0 &&
    form.partiesProductionType.length > 0 &&
    form.livestockLost !== '' &&
    Boolean(form.cause) &&
    form.humanCasualties !== '' &&
    Boolean(form.livelihoodImpact.trim()) &&
    // age_group_of_parties is shown to and required from R05 per the field
    // dictionary — unlike the fields below, it is never exempted.
    Boolean(form.ageGroupOfParties) &&
    // These fields are hidden from R05's simplified form entirely (per the
    // field dictionary's R05 visibility column) — only required once R04+
    // fills them in on the full flow.
    (simplified || (
      Boolean(form.sexOfPrimaryParties) &&
      Boolean(form.assetDamage) &&
      Boolean(form.reportingAuthority) &&
      form.estimatedEconomicLossNgn !== ''
    ));

  useEffect(() => {
    if (!row && mode === 'create') {
      setForm((prev) => ({
        ...prev,
        state: isR05 ? (user?.assignedState ?? '') : '',
        lga: isR05 ? (user?.assignedLga ?? '') : '',
        resolutionStatus: 'unresolved',
        severityLevel: 'low',
        firstOccurrence: 'yes_first',
        estimatedEconomicLossNgn: '',
        livelihoodImpact: '',
        evidenceAttachment: '',
        evidenceAttachmentFileName: '',
        partiesInvolved: [],
        partiesProductionType: [],
        sexOfPrimaryParties: '',
        ageGroupOfParties: '',
        humanCasualties: '',
        assetDamage: '',
        reportingAuthority: '',
        linkedCommitteeId: '',
      }));
      return;
    }

    if (row) {
      setForm({
        state: row.state ?? '',
        lga: row.lga ?? '',
        wardCommunity: row.ward_community ?? row.wardCommunity ?? '',
        incidentDate: row.incident_date ?? row.incidentDate ?? '',
        conflictType: row.conflict_type ?? row.conflictType ?? '',
        severityLevel: row.severity_level ?? row.severityLevel ?? 'low',
        resolutionStatus: row.resolution_status ?? row.resolutionStatus ?? 'unresolved',
        firstOccurrence: normalizeFirstOccurrence(row.first_occurrence ?? row.firstOccurrence),
        partiesInvolved: splitParties(row.parties_involved ?? row.partiesInvolved),
        partiesProductionType: splitParties(row.parties_production_type ?? row.partiesProductionType),
        sexOfPrimaryParties: row.sex_of_primary_parties ?? row.sexOfPrimaryParties ?? '',
        ageGroupOfParties: row.age_group_of_parties ?? row.ageGroupOfParties ?? '',
        cause: row.cause ?? row.causeOfConflict ?? '',
        livestockLost: String(row.livestock_lost ?? row.livestockLost ?? ''),
        humanCasualties: String(row.human_casualties ?? row.humanCasualties ?? ''),
        estimatedEconomicLossNgn: String(row.estimated_economic_loss_ngn ?? row.estimatedEconomicLossNgn ?? ''),
        livelihoodImpact: row.livelihood_impact ?? row.livelihoodImpact ?? '',
        evidenceAttachment: row.evidence_attachment ?? row.evidenceAttachment ?? '',
        evidenceAttachmentFileName: row.evidence_attachment_file_name ?? row.evidenceAttachmentFileName ?? '',
        assetDamage: row.asset_damage ?? row.assetDamage ?? '',
        reportingAuthority: row.reporting_authority ?? row.reportingAuthority ?? '',
        linkedCommitteeId: row.linked_committee_id ?? row.linkedCommitteeId ?? '',
      });
    }
  }, [mode, row, isR05, user?.assignedState, user?.assignedLga]);

  const setValue = useCallback((key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'state' && !lockedLocation) {
        next.lga = '';
        next.wardCommunity = '';
      }
      if (key === 'lga' && !lockedLocation) {
        next.wardCommunity = '';
      }
      return next;
    });
  }, [lockedLocation]);

  if (mode === 'view') {
    const dotCls = VIEW_STATUS_DOT[currentDataStatus] ?? VIEW_STATUS_DOT.draft;
    const pillCls = VIEW_STATUS_PILL[currentDataStatus] ?? VIEW_STATUS_PILL.draft;
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-4xl mx-auto">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Record Details</h2>
            <p className="text-sm text-gray-400 mt-1">Review the information for this record</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500">Status:</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize ${pillCls}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
              {currentDataStatus}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <SectionHead title="Incident Classification" />
          <ReviewRow label="Conflict Type" value={form.conflictType} />
          <ReviewRow label="Severity Level" value={form.severityLevel} />
          {!simplified && <ReviewRow label="Resolution Status" value={form.resolutionStatus} />}
          <ReviewRow label="First Occurrence" value={form.firstOccurrence} />

          <SectionHead title="Parties & Impact" />
          <ReviewRow label="Parties Involved" value={joinParties(form.partiesInvolved)} />
          <ReviewRow label="Parties Production Type" value={joinParties(form.partiesProductionType)} />
          {!simplified && <ReviewRow label="Sex of Primary Parties" value={form.sexOfPrimaryParties} />}
          <ReviewRow label="Age Group of Parties" value={form.ageGroupOfParties} />
          <ReviewRow label="Livestock Lost" value={form.livestockLost} />
          <ReviewRow label="Human Casualties" value={form.humanCasualties} />
          {!simplified && <ReviewRow label="Asset Damage" value={form.assetDamage} />}
          {!simplified && <ReviewRow label="Estimated Loss (NGN)" value={form.estimatedEconomicLossNgn} />}
          <ReviewRow label="Livelihood Impact" value={form.livelihoodImpact} />
          {!simplified && <ReviewRow label="Reporting Authority" value={form.reportingAuthority} />}
          <ReviewRow label="Linked Committee" value={committeeOptions.find((c) => c.value === form.linkedCommitteeId)?.label || form.linkedCommitteeId} />
          <ReviewRow label="Evidence Attachment" value={form.evidenceAttachment ? (form.evidenceAttachmentFileName || 'File attached') : ''} />

          <SectionHead title="Geospatial" />
          <ReviewRow label="State" value={form.state} />
          <ReviewRow label="LGA" value={form.lga} />
          <ReviewRow label="Ward / Community" value={form.wardCommunity} />

          <SectionHead title="Incident Details" />
          <ReviewRow label="Incident Date" value={form.incidentDate} />
          <ReviewRow label="Cause" value={form.cause} />
          {!simplified && <ReviewRow label="Data Flow Validation Level" value={row?.data_flow_validation_level ?? row?.dataFlowValidationLevel ?? 'LGA'} />}
        </div>

        <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
            Back
          </button>
          {canEdit && (
            <button type="button" onClick={onEdit} className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700">
              Edit Record
            </button>
          )}
        </div>
      </div>
    );
  }

  const submit = async (opts = {}) => {
    if (saving) return;

    if (!form.state || !form.lga || !form.incidentDate || !form.conflictType || !form.wardCommunity.trim() || form.partiesInvolved.length === 0 || !form.cause || form.humanCasualties === '' || !form.livelihoodImpact.trim()) {
      setError('State, LGA, Incident Date, Conflict Type, Ward/Community, Parties Involved, Cause, Human Casualties and Livelihood Impact are required.');
      return;
    }

    const lgaMatch = lgaOptions.some((option) => option.value === form.lga);
    if (!lgaMatch) {
      setError(`No geospatial reference row for state=${form.state}, lga=${form.lga}.`);
      return;
    }

    const payload = buildConflictRecordPayload({
      state: form.state,
      lga: form.lga || null,
      ward_community: form.wardCommunity,
      incidentDate: form.incidentDate,
      conflictType: form.conflictType,
      severityLevel: form.severityLevel,
      resolutionStatus: simplified ? (row?.resolution_status ?? row?.resolutionStatus ?? 'unresolved') : form.resolutionStatus,
      partiesInvolved: form.partiesInvolved,
      partiesProductionType: form.partiesProductionType,
      sexOfPrimaryParties: form.sexOfPrimaryParties,
      ageGroupOfParties: form.ageGroupOfParties,
      cause: form.cause,
      livestockLost: form.livestockLost,
      humanCasualties: form.humanCasualties,
      estimatedEconomicLossNgn: simplified
        ? (row?.estimated_economic_loss_ngn ?? row?.estimatedEconomicLossNgn ?? null)
        : form.estimatedEconomicLossNgn,
      livelihoodImpact: form.livelihoodImpact,
      evidenceAttachment: form.evidenceAttachment,
      evidenceAttachmentFileName: form.evidenceAttachmentFileName,
      assetDamage: form.assetDamage,
      reportingAuthority: form.reportingAuthority,
      linkedCommitteeId: form.linkedCommitteeId,
      firstOccurrence: form.firstOccurrence,
      dataFlowValidationLevel: row?.data_flow_validation_level ?? row?.dataFlowValidationLevel ?? 'lga',
    });

    setSaving(true);
    setError('');
    try {
      if (mode === 'create') {
        await conflictsApi.records.create(payload);
      } else if (row?.uuid) {
        await conflictsApi.records.update(row.uuid, payload);
        if (opts.submitAfterSave) {
          await conflictsApi.records.submit(row.uuid);
        }
      }
      toast.success(
        mode === 'create'
          ? 'Conflict record created.'
          : (opts.submitAfterSave ? 'Conflict record submitted for review.' : 'Conflict record updated.'),
        { position: 'top-right', autoClose: 3000 }
      );
      await onSaved?.();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Unable to save conflict record.';
      setError(msg);
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <button
        onClick={() => setStep((current) => Math.max(0, current - 1))}
        className="h-11 flex-1 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50"
      >
        Previous
      </button>
      {mode === 'edit' && isFieldUser ? (
        <>
          <button
            onClick={() => submit()}
            disabled={saving}
            className="h-11 flex-1 rounded-full border border-green-300 bg-white text-sm font-semibold text-green-700 hover:bg-green-50 disabled:opacity-60"
          >
            {saving ? 'Saving...' : (currentDataStatus === 'draft' ? 'Save as Draft' : 'Save Changes')}
          </button>
          <button
            onClick={() => submit({ submitAfterSave: true })}
            disabled={saving}
            className="h-11 flex-1 rounded-full bg-blue-600 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? 'Submitting...' : 'Submit for Review'}
          </button>
        </>
      ) : (
        <button
          onClick={() => submit()}
          disabled={saving}
          className="h-11 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
        >
          {saving ? 'Saving...' : (mode === 'create' ? 'Create Record' : 'Save Changes')}
        </button>
      )}
    </>
  );

  return (
    <RecordWizard
      steps={[
        { key: 'details', label: 'Conflict Incident' },
        { key: 'review', label: 'Review' },
      ]}
      currentStep={step}
      title={mode === 'create' ? 'Add New Record' : 'Edit Record'}
      subtitle={mode === 'create' ? 'Fill in the details to create a new record' : 'Update the details for this record'}
      statusLabel={currentDataStatus}
      onCancel={onClose}
      onBack={() => setStep((current) => Math.max(0, current - 1))}
      onNext={() => setStep(1)}
      canGoNext={canGoNext}
      nextLabel="Review"
      footer={isReviewStep ? footer : undefined}
      error={error}
    >
      {!isReviewStep && modulePanel}
      {isReviewStep ? (
        <>
          <SectionHead title="Incident Classification" />
          <ReviewRow label="Conflict Type" value={form.conflictType} />
          <ReviewRow label="Severity Level" value={form.severityLevel} />
          {!simplified && <ReviewRow label="Resolution Status" value={form.resolutionStatus} />}
          <ReviewRow label="First Occurrence" value={form.firstOccurrence} />

          <SectionHead title="Parties & Impact" />
          <ReviewRow label="Parties Involved" value={joinParties(form.partiesInvolved)} />
          <ReviewRow label="Parties Production Type" value={joinParties(form.partiesProductionType)} />
          {!simplified && <ReviewRow label="Sex of Primary Parties" value={form.sexOfPrimaryParties} />}
          <ReviewRow label="Age Group of Parties" value={form.ageGroupOfParties} />
          <ReviewRow label="Livestock Lost" value={form.livestockLost} />
          <ReviewRow label="Human Casualties" value={form.humanCasualties} />
          {!simplified && <ReviewRow label="Asset Damage" value={form.assetDamage} />}
          {!simplified && <ReviewRow label="Estimated Loss (NGN)" value={form.estimatedEconomicLossNgn} />}
          <ReviewRow label="Livelihood Impact" value={form.livelihoodImpact} />
          {!simplified && <ReviewRow label="Reporting Authority" value={form.reportingAuthority} />}
          <ReviewRow label="Linked Committee" value={committeeOptions.find((c) => c.value === form.linkedCommitteeId)?.label || form.linkedCommitteeId} />
          <ReviewRow label="Evidence Attachment" value={form.evidenceAttachment ? (form.evidenceAttachmentFileName || 'File attached') : ''} />

          <SectionHead title="Geospatial" />
          <ReviewRow label="State" value={form.state} />
          <ReviewRow label="LGA" value={form.lga} />
          <ReviewRow label="Ward / Community" value={form.wardCommunity} />

          <SectionHead title="Incident Details" />
          <ReviewRow label="Incident Date" value={form.incidentDate} />
          <ReviewRow label="Cause" value={form.cause} />
          {!simplified && <ReviewRow label="Data Flow Validation Level" value={row?.data_flow_validation_level ?? row?.dataFlowValidationLevel ?? 'LGA'} />}
        </>
      ) : (
        <div className="space-y-5">

          <section className="space-y-3">
            <SectionHead title="Incident Classification" />
            <div className="grid grid-cols-2 gap-4">
              <Select label="Conflict Type *" required value={form.conflictType} onChange={setValue('conflictType')} options={CONFLICT_TYPE_OPTIONS} />
              <Select label="Severity Level" value={form.severityLevel} onChange={setValue('severityLevel')} options={SEVERITY_OPTIONS} />
              {!simplified && <Select label="Resolution Status" value={form.resolutionStatus} onChange={setValue('resolutionStatus')} options={RESOLUTION_OPTIONS} />}
              <Select label="First Occurrence" value={form.firstOccurrence} onChange={setValue('firstOccurrence')} options={FIRST_OCCURRENCE_OPTIONS} />
            </div>
          </section>

          <SectionDivider />

          <section className="space-y-3">
            <SectionHead title="Parties & Impact" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Parties Involved" required span2>
                <TogglePills
                  values={form.partiesInvolved}
                  onChange={(values) => setValue('partiesInvolved')(values)}
                  options={PARTIES_INVOLVED_TYPE_OPTIONS}
                />
              </Field>
              <Field label="Parties Production Type *" required span2>
                <TogglePills
                  values={form.partiesProductionType}
                  onChange={(values) => setValue('partiesProductionType')(values)}
                  options={PARTIES_PRODUCTION_OPTIONS}
                />
              </Field>
              {!simplified && (
                <Select label="Sex of Primary Parties *" required value={form.sexOfPrimaryParties} onChange={setValue('sexOfPrimaryParties')} options={SEX_OPTIONS} />
              )}
              <Select label="Age Group of Parties *" required value={form.ageGroupOfParties} onChange={setValue('ageGroupOfParties')} options={AGE_GROUP_OPTIONS} />
              <TextInput label="Livestock Lost *" required type="number" min="0" value={form.livestockLost} onChange={setValue('livestockLost')} />
              <TextInput label="Human Casualties *" required type="number" min="0" value={form.humanCasualties} onChange={setValue('humanCasualties')} />
              {!simplified && (
                <Select label="Asset Damage *" required value={form.assetDamage} onChange={setValue('assetDamage')} options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} />
              )}
              {!simplified && (
                <TextInput label="Estimated Loss (NGN) *" required type="number" value={form.estimatedEconomicLossNgn} onChange={setValue('estimatedEconomicLossNgn')} />
              )}
              {!simplified && (
                <Select label="Reporting Authority *" required value={form.reportingAuthority} onChange={setValue('reportingAuthority')} options={REPORTING_AUTHORITY_OPTIONS} />
              )}
              <Select label="Linked Committee" value={form.linkedCommitteeId} onChange={setValue('linkedCommitteeId')} options={committeeOptions} placeholder="Select committee (optional)" span2 />
              <TextArea
                label="Livelihood Impact *"
                required
                value={form.livelihoodImpact}
                onChange={setValue('livelihoodImpact')}
                placeholder="Describe the impact on livelihoods"
                span2
              />
              <div className="col-span-2">
                <FileUploadField
                  label="Evidence Attachment"
                  value={form.evidenceAttachment}
                  fileName={form.evidenceAttachmentFileName}
                  onChange={(dataUri, name) => {
                    setValue('evidenceAttachment')(dataUri);
                    setValue('evidenceAttachmentFileName')(name);
                  }}
                  accept="image/*,.pdf"
                  hint="Image or PDF — max 10MB, single file"
                />
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <SectionHead title="Geospatial" />
            <div className="grid grid-cols-2 gap-4">
              {!lockedLocation ? (
                <>
                  <Select label="State *" required value={form.state} onChange={setValue('state')} options={stateOptions} placeholder="Select state" />
                  <Select
                    label="LGA *"
                    required
                    value={form.lga}
                    onChange={setValue('lga')}
                    options={lgaOptions}
                    placeholder={form.state ? (loadingLgas ? 'Loading LGAs...' : 'Select LGA') : 'Select state first'}
                  />
                </>
              ) : (
                <>
                  <Field label="State *" required>
                    <ReadonlyBox>{form.state || 'Not set'}</ReadonlyBox>
                  </Field>
                  <Field label="LGA *" required>
                    <ReadonlyBox>{form.lga || 'Not set'}</ReadonlyBox>
                  </Field>
                </>
              )}

              <Select
                label="Ward / Community *"
                required
                value={form.wardCommunity}
                onChange={setValue('wardCommunity')}
                options={wardOptions}
                placeholder={form.lga ? (loadingWards ? 'Loading wards...' : 'Select Ward / Community') : 'Select LGA first'}
                readOnly={!form.lga || loadingWards}
                span2
              />
            </div>
          </section>

          <SectionDivider />

          <section className="space-y-3">
            <SectionHead title="Incident Details" />
            <div className="grid grid-cols-2 gap-4">
              <TextInput label="Incident Date *" required type="date" value={form.incidentDate} onChange={setValue('incidentDate')} />
              <Select
                label="Cause"
                required
                value={form.cause}
                onChange={setValue('cause')}
                options={CAUSE_OF_CONFLICT_OPTIONS}
                placeholder="Select cause of conflict"
                span2
              />
              {!simplified && (
                <Field label="Data Flow Validation Level">
                  <ReadonlyBox>{row?.data_flow_validation_level ?? row?.dataFlowValidationLevel ?? 'LGA'}</ReadonlyBox>
                </Field>
              )}
            </div>
          </section>
        </div>
      )}
    </RecordWizard>
  );
}

const EMPTY_FILTERS = {
  conflictType: '',
  severityLevel: '',
  resolutionStatus: '',
  state: '',
};

const BASE_COLUMNS = [
  { title: 'ID', data: 'uuid', sortable: true, defaultContent: '' },
  { title: 'State', data: 'state', sortable: true, defaultContent: '' },
  { title: 'LGA', data: 'lga', sortable: true, defaultContent: '' },
  { title: 'Ward / Community', data: 'wardCommunity', sortable: true, defaultContent: '' },
  { title: 'Incident Date', data: 'incidentDate', sortable: true, defaultContent: '', render: (data, type) => (type === 'display' ? formatDate(data) : (data ?? '')) },
  { title: 'Conflict Type', data: 'conflictType', sortable: true, defaultContent: '' },
  { title: 'Severity', data: 'severityLevel', sortable: true, defaultContent: '', render: badgeHtml },
  { title: 'Parties Involved', data: 'parties_involved', sortable: false, defaultContent: '', render: (data, type, row) => (type === 'display' ? displayList(row.parties_involved ?? row.partiesInvolved) : joinParties(row.parties_involved ?? row.partiesInvolved)) },
  { title: 'Cause', data: 'causeOfConflict', sortable: true, defaultContent: '' },
  { title: 'Livestock Lost', data: 'livestockLost', sortable: true, defaultContent: '' },
  { title: 'Human Casualties', data: 'humanCasualties', sortable: true, defaultContent: '' },
  { title: 'Resolution Status', data: 'resolutionStatus', sortable: true, defaultContent: '', render: badgeHtml },
  { title: 'Data Status', data: 'validationStatus', sortable: true, defaultContent: '', render: badgeHtml },
  { title: 'Action', data: null, sortable: false, defaultContent: '', render: () => actionCellHtml() },
];

export default function ConflictRecordsTab() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleId = user?.roleId ?? '';
  const isR05 = roleId === 'R05';
  const isFieldUser = roleId === 'R04' || roleId === 'R05';
  const canManageConflictRecords = !['R02', 'R03', 'R06'].includes(roleId);
  const { stateOptions } = useLocationOptions();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [quickBusy, setQuickBusy] = useState(false);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await conflictsApi.records.listAll({ per_page: 1000, page: 1 });
      setRecords(Array.isArray(result?.items) ? result.items : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Unable to load conflict records.';
      setError(msg);
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useCollectionSync(conflictsApi.records.collectionKey, loadRecords);

  const filterConfig = useMemo(() => [
    { key: 'conflictType', label: 'Conflict Type', options: CONFLICT_TYPE_OPTIONS, defaultLabel: 'All' },
    { key: 'severityLevel', label: 'Severity Level', options: SEVERITY_OPTIONS, defaultLabel: 'All' },
    { key: 'resolutionStatus', label: 'Resolution Status', options: RESOLUTION_OPTIONS, defaultLabel: 'All' },
    { key: 'state', label: 'State', options: stateOptions, defaultLabel: 'All' },
  ], [stateOptions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return records.filter((record) => {
      const searchable = [
        record.uuid,
        record.state,
        record.lga,
        record.wardCommunity,
        record.conflictType,
        record.severityLevel,
        record.resolutionStatus,
        joinParties(record.parties_involved ?? record.partiesInvolved),
      ].filter(Boolean).join(' ').toLowerCase();

      const matchSearch = !q || searchable.includes(q);
      const matchConflictType = !filters.conflictType || record.conflict_type === filters.conflictType;
      const matchSeverity = !filters.severityLevel || record.severity_level === filters.severityLevel;
      const matchResolution = !filters.resolutionStatus || record.resolution_status === filters.resolutionStatus;
      const matchState = !filters.state || record.state === filters.state;

      return matchSearch && matchConflictType && matchSeverity && matchResolution && matchState;
    });
  }, [records, filters, search]);

  const columns = useMemo(() => BASE_COLUMNS, []);

  const handleRowAction = useCallback((action, row, meta) => {
    const rect = anchorRect(meta?.button);
    if (action === 'menu' || action == null) setActionMenu({ row, rect });
  }, []);

  const runQuickAction = useCallback(async (row, method) => {
    setQuickBusy(true);
    try {
      await conflictsApi.records[method](row.uuid);
      await loadRecords();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || `Failed to ${method} record.`;
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
    } finally {
      setQuickBusy(false);
    }
  }, [loadRecords]);

  const handleRejectConfirm = useCallback(async (notes) => {
    if (!rejectTarget) return;
    setQuickBusy(true);
    try {
      await conflictsApi.records.reject(rejectTarget.uuid, notes);
      await loadRecords();
      setRejectTarget(null);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to reject record.';
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
      throw err;
    } finally {
      setQuickBusy(false);
    }
  }, [rejectTarget, loadRecords]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setQuickBusy(true);
    try {
      await conflictsApi.records.remove(deleteTarget.uuid);
      await loadRecords();
      setDeleteTarget(null);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete record.';
      toast.error(msg, { position: 'top-right', autoClose: 5000 });
    } finally {
      setQuickBusy(false);
    }
  }, [deleteTarget, loadRecords]);

  if (error && records.length === 0 && !loading) {
    return (
      <section className="rounded-xl border border-red-100 bg-red-50 px-6 py-10 text-sm text-red-700">
        {error}
      </section>
    );
  }

  return (
    <div>
      {!isR05 && (
        <FilterBar
          filters={filterConfig}
          filterValues={filters}
          onFilterChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
          onApply={() => {}}
        />
      )}

      <SearchRow
        searchValue={search}
        onSearch={setSearch}
        onCreate={() => navigate('/conflicts-dispute/new?tab=records')}
        canCreate={canManageConflictRecords}
      />

      {loading && records.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-sm text-gray-500">
          Loading conflict records...
        </div>
      ) : (
        <NlimsDataTable
          tableId="conflict-records-table"
          columns={columns}
          data={filtered}
          serverSide={false}
          hideSearch
          pageLength={10}
          enableRowSelection
          onRowAction={handleRowAction}
        />
      )}

      {actionMenu && !quickBusy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.conflictRecords}
          statusField="dataStatus"
          onClose={() => setActionMenu(null)}
          onView={() => { navigate(`/conflicts-dispute/view/${actionMenu.row.uuid}?tab=records`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onEdit={() => { navigate(`/conflicts-dispute/edit/${actionMenu.row.uuid}?tab=records`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onSubmit={() => runQuickAction(actionMenu.row, 'submit')}
          onValidate={() => runQuickAction(actionMenu.row, 'validate')}
          onApprove={() => runQuickAction(actionMenu.row, 'approve')}
          onReject={() => { setRejectTarget(actionMenu.row); setActionMenu(null); }}
          onDelete={() => { setDeleteTarget(actionMenu.row); setActionMenu(null); }}
        />
      )}

      {rejectTarget && (
        <RejectNotesModal
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          row={deleteTarget}
          entityLabel={(r) => [r?.conflictType, r?.state, r?.lga].filter(Boolean).join(' — ')}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={quickBusy}
        />
      )}

      {createOpen && (
        <ConflictEditorModal
          mode="create"
          row={null}
          isR05={isR05}
          isFieldUser={isFieldUser}
          onClose={() => setCreateOpen(false)}
          onSaved={loadRecords}
        />
      )}
    </div>
  );
}
